import API_CONFIG from "../config/api";

class MarketplaceService {
  // Get all products
  static async getAllProducts() {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/products`);
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to fetch products");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  }

  // Get products by category
  static async getProductsByCategory(category) {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/products?category=${encodeURIComponent(
          category
        )}`
      );
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to fetch products by category");
      }
    } catch (error) {
      console.error("Error fetching products by category:", error);
      throw error;
    }
  }

  // Search products
  static async searchProducts(searchTerm) {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/products?search=${encodeURIComponent(
          searchTerm
        )}`
      );
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to search products");
      }
    } catch (error) {
      console.error("Error searching products:", error);
      throw error;
    }
  }

  // Get single product by ID
  static async getProductById(id) {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/products/${id}`);
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to fetch product");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      throw error;
    }
  }

  // Create new product (admin only)
  static async createProduct(productData) {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(productData),
      });

      const data = await response.json();

      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || "Failed to create product");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      throw error;
    }
  }

  // Update product (admin only)
  static async updateProduct(id, productData) {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/products/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(productData),
        }
      );

      const data = await response.json();

      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || "Failed to update product");
      }
    } catch (error) {
      console.error("Error updating product:", error);
      throw error;
    }
  }

  // Delete product (admin only)
  static async deleteProduct(id) {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/products/${id}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || "Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      throw error;
    }
  }

  // Get product categories
  static async getCategories() {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/products/categories`
      );
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  }

  // Cart management (still using localStorage for now)
  static getCart() {
    try {
      const cart = localStorage.getItem("marketplaceCart");
      return cart ? JSON.parse(cart) : [];
    } catch (error) {
      console.error("Error getting cart:", error);
      return [];
    }
  }

  static saveCart(cart) {
    try {
      localStorage.setItem("marketplaceCart", JSON.stringify(cart));
    } catch (error) {
      console.error("Error saving cart:", error);
    }
  }

  static addToCart(product, quantity = 1) {
    try {
      const cart = this.getCart();
      const existingItemIndex = cart.findIndex(
        (item) => item.productId === product.id
      );

      if (existingItemIndex >= 0) {
        cart[existingItemIndex].quantity += quantity;
      } else {
        cart.push({
          productId: product.id,
          name: product.name,
          price: product.price,
          quantity: quantity,
          imageUrl: product.imageUrl,
          unit: product.unit,
        });
      }

      this.saveCart(cart);
      return cart;
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  }

  static removeFromCart(productId) {
    try {
      const cart = this.getCart();
      const filteredCart = cart.filter((item) => item.productId !== productId);
      this.saveCart(filteredCart);
      return filteredCart;
    } catch (error) {
      console.error("Error removing from cart:", error);
      throw error;
    }
  }

  static updateCartQuantity(productId, quantity) {
    try {
      const cart = this.getCart();
      const itemIndex = cart.findIndex((item) => item.productId === productId);

      if (itemIndex >= 0) {
        if (quantity <= 0) {
          cart.splice(itemIndex, 1);
        } else {
          cart[itemIndex].quantity = quantity;
        }
        this.saveCart(cart);
      }

      return cart;
    } catch (error) {
      console.error("Error updating cart quantity:", error);
      throw error;
    }
  }

  static clearCart() {
    try {
      localStorage.removeItem("marketplaceCart");
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  }
}

export default MarketplaceService;
